import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./UserOrdersInfo.css";
import { FaChevronLeft, FaChevronRight, FaBox, FaShippingFast, FaInfoCircle } from "react-icons/fa";

const UserOrdersInfo = () => {
  const { id } = useParams();
  const [ordersData, setOrdersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 5;
  
  // Base64 encoded placeholder image (light gray with "No Image" text)
  const placeholderImage = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiBmaWxsPSIjZjFmMWYxIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxMCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iIGZpbGw9IiM4ODg4ODgiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==";

  // Fetch data from the API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("admintoken");
        const response = await fetch(
          `https://admin.riymay.com/api/v1/admin/user/orders/info/${id}?page=${page}&limit=${limit}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch orders: ${response.status}`);
        }
        
        const data = await response.json();
        setOrdersData(data);
        setError(null);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to load orders. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrders();
  }, [id, page]);

  const handleNextPage = () => {
    if (ordersData && ordersData.hasMore) {
      setPage((prevPage) => prevPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage((prevPage) => prevPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getStatusClass = (status) => {
    switch(status.toLowerCase()) {
      case 'delivered':
        return 'status-delivered';
      case 'shipped':
        return 'status-shipped';
      case 'processing':
        return 'status-processing';
      case 'cancelled':
        return 'status-cancelled';
      case 'pending':
        return 'status-pending';
      default:
        return '';
    }
  };

  const getPaymentStatusClass = (status) => {
    switch(status.toLowerCase()) {
      case 'paid':
        return 'payment-paid';
      case 'pending':
        return 'payment-pending';
      case 'failed':
        return 'payment-failed';
      default:
        return '';
    }
  };

  if (loading && !ordersData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <FaInfoCircle className="error-icon" />
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="user-orders-container">
      <div className="orders-header">
        <h1>
          <FaBox className="header-icon" />
          Orders Information
        </h1>
        {ordersData && ordersData.ordersInfoArray && (
          <div className="orders-summary">
            <p>Showing {ordersData.ordersInfoArray.length} of {ordersData.totalOrders} orders</p>
            <p>Page {page} {ordersData.hasMore ? 'of many' : ''}</p>
          </div>
        )}
      </div>

      {ordersData && ordersData.ordersInfoArray && ordersData.ordersInfoArray.length > 0 ? (
        <div className="orders-grid">
          {ordersData.ordersInfoArray.map((order, index) => (
            <div key={order.ordersInfo._id} className="order-card">
              <div className="order-card-header">
                <div className="order-number">
                  <span>Order #{index + 1 + (page - 1) * limit}</span>
                  <span className="order-date">{formatDate(order.ordersInfo.orderPlacedDate)}</span>
                </div>
                <div className="order-id">ID: {order.ordersInfo._id.substring(order.ordersInfo._id.length - 8)}</div>
              </div>

              <div className="order-status-container">
                <div className={`order-status ${getStatusClass(order.ordersInfo.orderStatus)}`}>
                  {order.ordersInfo.orderStatus}
                </div>
                <div className={`payment-status ${getPaymentStatusClass(order.ordersInfo.orderPaymentStatus)}`}>
                  {order.ordersInfo.orderPaymentStatus}
                </div>
                <div className="order-amount">₹{order.ordersInfo.totalOrderAmount.toLocaleString()}</div>
              </div>

              <div className="order-content">
                <div className="order-items-section">
                  <h3>
                    <FaBox className="summary-icon" />
                    Order Items ({order.orderItems.length})
                  </h3>
                  
                  <div className="order-items-table-container">
  <table className="order-items-table1">
    <thead>
      <tr className="order-item-row">
        <th className="product-col">Product</th>
        <th className="qty-col">Qty</th>
        <th className="price-col">Price</th>
        <th className="total-col">Total</th>
        <th className="image-col">Image</th>
      </tr>
    </thead>
    <tbody>
      {order.orderItems.map((item) => (
        <tr key={item._id} className="order-item-row">
          <td className="product-col" data-label="Product" title={item.productName}>
            {item.productName.length > 25 
              ? `${item.productName.substring(0, 25)}...` 
              : item.productName}
          </td>
          <td className="qty-col" data-label="Quantity">{item.quantity}</td>
          <td className="price-col" data-label="Price">₹{item.unitPrice.toLocaleString()}</td>
          <td className="total-col" data-label="Total">₹{item.totalPrice.toLocaleString()}</td>
          <td className="image-col" data-label="Image">
            <img
              src={`https://riymay.com/assets/${item.productUrl}`}
              alt={item.productName}
              onError={(e) => {
                e.target.onerror = null; // Prevent infinite loop
                e.target.src = placeholderImage;
              }}
            />
          </td>
        </tr>
      ))}
    </tbody>
  </table>
</div>
                </div>

                <div className="shipping-info">
                  <h3>
                    <FaShippingFast className="shipping-icon" />
                    Shipping Address
                  </h3>
                  {order.orderShippingInfo.orderShippingAddress.map((address) => (
                    <div key={address._id} className="shipping-address">
                      <p className="address-line">{address.street}</p>
                      <p className="address-line">{address.city}, {address.state} {address.pinCode}</p>
                      <p className="address-line">{address.country}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-orders">
          <p>No orders found for this user.</p>
        </div>
      )}

      {ordersData && ordersData.ordersInfoArray && ordersData.ordersInfoArray.length > 0 && (
        <div className="pagination-controls">
          <button
            className="pagination-button prev-button"
            onClick={handlePreviousPage}
            disabled={page === 1}
          >
            <FaChevronLeft className="button-icon" />
            Previous
          </button>
          <div className="pagination-info">
            Page {page} {ordersData.hasMore ? '...' : `of ${Math.ceil(ordersData.totalOrders / limit)}`}
          </div>
          <button
            className="pagination-button next-button"
            onClick={handleNextPage}
            disabled={!ordersData.hasMore}
          >
            Next
            <FaChevronRight className="button-icon" />
          </button>
        </div>
      )}
    </div>
  );
};

export default UserOrdersInfo;